import { useMemo, lazy, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { 
  Bot, 
  Building2, 
  ArrowRight, 
  TrendingUp, 
  Users, 
  DollarSign,
  Lightbulb,
  Target,
  Zap,
  Store
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { HeroVisual } from "@/components/HeroVisual";
import { AnimatedSection } from "@/components/AnimatedSection";
import { FloatingSectionNav } from "@/components/FloatingSectionNav";
import { useRobots } from "@/hooks/useRobots";
import { useCompanies } from "@/hooks/useCompanies";
import { Button } from "@/components/ui/button";
import { useAnimatedCounter } from "@/hooks/useAnimatedCounter";
import { parseUnitsDeployed } from "@/lib/robot-utils";

// Lazy load below-the-fold components to reduce initial JS
const FeaturedRobots = lazy(() => import("@/components/FeaturedRobots").then(m => ({ default: m.FeaturedRobots })));
const AIRecommenderCTA = lazy(() => import("@/components/AIRecommenderCTA").then(m => ({ default: m.AIRecommenderCTA })));
const MarketplacePreview = lazy(() => import("@/components/MarketplacePreview").then(m => ({ default: m.MarketplacePreview })));
const NewsFeedPreview = lazy(() => import("@/components/NewsFeedPreview").then(m => ({ default: m.NewsFeedPreview })));
const EventsPreview = lazy(() => import("@/components/EventsPreview").then(m => ({ default: m.EventsPreview })));

const Index = () => {
  const { data: robots = [], isLoading: robotsLoading } = useRobots();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const queryClient = useQueryClient();
  
  const isLoading = robotsLoading || companiesLoading;

  // Pre-fetch news for faster /news page loading
  useEffect(() => {
    // Prefetch curated news from database
    queryClient.prefetchQuery({
      queryKey: ['curated-news'],
      queryFn: async () => {
        const { data } = await supabase
          .from('news_articles')
          .select(`
            id,
            title,
            description,
            url,
            image_url,
            published_at,
            author,
            source_id,
            news_sources!inner(name, is_active)
          `)
          .eq('is_hidden', false)
          .order('published_at', { ascending: false, nullsFirst: false })
          .limit(50);
        
        return (data || []).map((article: any) => ({
          id: article.id,
          title: article.title,
          description: article.description || '',
          url: article.url,
          source: article.news_sources?.name || 'Unknown',
          publishedAt: article.published_at,
          image: article.image_url,
          sourceType: 'rss' as const,
        }));
      },
      staleTime: 5 * 60 * 1000,
    });

    // Prefetch search news (default query)
    queryClient.prefetchQuery({
      queryKey: ['search-news', 'humanoid robots'],
      queryFn: async () => {
        const { data, error } = await supabase.functions.invoke('fetch-robotics-news', {
          body: { query: 'humanoid robots', forceRefresh: false },
        });
        if (error) throw error;
        return {
          articles: (data.articles || []).map((a: any) => ({ ...a, sourceType: 'search' })),
          fetchedAt: data.fetchedAt,
          cached: data.cached || false,
          expiresAt: data.expiresAt,
        };
      },
      staleTime: 5 * 60 * 1000,
    });
  }, [queryClient]);

  // Calculate stats
  const stats = useMemo(() => {
    const uniqueCountries = new Set(robots.map(r => r.country)).size;
    const totalDeployments = robots.reduce((sum, r) => sum + parseUnitsDeployed(r.unitsDeployed), 0);
    const commercialRobots = robots.filter(r => r.status === 'production').length;
    
    return {
      robotCount: robots.length,
      companyCount: companies.length,
      countryCount: uniqueCountries,
      deploymentCount: totalDeployments,
      commercialCount: commercialRobots,
    };
  }, [robots, companies]);

  const { count: animatedRobots } = useAnimatedCounter(stats.robotCount, 1200);
  const { count: animatedCompanies } = useAnimatedCounter(stats.companyCount, 1200);
  const { count: animatedCountries } = useAnimatedCounter(stats.countryCount, 1200);
  const { count: animatedDeployments } = useAnimatedCounter(stats.deploymentCount, 1500);

  const trends = [
    {
      slug: "labor-shortage",
      icon: Users,
      title: "Labor Shortage",
      description: "Global workforce gaps driving automation demand in manufacturing and logistics"
    },
    {
      slug: "aging-population",
      icon: TrendingUp,
      title: "Aging Population",
      description: "Elder care and healthcare sectors accelerating humanoid adoption"
    },
    {
      slug: "cost-curves",
      icon: DollarSign,
      title: "Cost Curves",
      description: "Component costs dropping 15-20% annually, making humanoids economically viable"
    }
  ];

  const entryPoints = [
    {
      icon: Bot,
      title: "Explore Robots",
      description: "Browse the complete humanoid catalog with detailed specs and capabilities",
      href: "/robots",
      color: "bg-blue-500/10 text-blue-600 dark:text-blue-400"
    },
    {
      icon: Building2,
      title: "Explore Companies",
      description: "Discover who's building, funding, and scaling humanoid robotics",
      href: "/companies",
      color: "bg-purple-500/10 text-purple-600 dark:text-purple-400"
    },
    {
      icon: Store,
      title: "Skill Marketplace",
      description: "Deploy certified skills or hire humanoid robots for your operations",
      href: "/marketplace",
      color: "bg-teal-500/10 text-teal-600 dark:text-teal-400",
      highlight: true
    },
    {
      icon: Lightbulb,
      title: "Explore Opportunities",
      description: "Find strategic insights on underserved markets and emerging niches",
      href: "/opportunities",
      color: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
    }
  ];

  // JSON-LD for homepage
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Lineroid",
      "url": "https://lineroid.com",
      "description": "The global intelligence platform and marketplace for humanoid robotics. Explore robots, deploy certified skills, hire humanoids, and discover strategic opportunities.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://lineroid.com/robots?search={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Lineroid",
      "url": "https://lineroid.com",
      "description": "The global intelligence platform and skill marketplace for humanoid robotics"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Humanoid Robots Database, Skill Marketplace & Intelligence Platform"
        description="Explore the global humanoid robots database. Deploy certified skills, hire robots for your operations, and discover strategic opportunities in the humanoid robotics industry."
        url="/"
        jsonLd={jsonLd}
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden min-h-[70vh] flex items-center">
          {/* Futuristic Background */}
          <HeroVisual />

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm font-mono text-cyan-400 uppercase tracking-widest mb-4">
                Humanoid Intelligence & Marketplace
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight">
                The Global Platform for{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                  Humanoid Robotics
                </span>
              </h1>
              <p className="text-base md:text-lg text-slate-400 max-w-xl mx-auto mb-12 leading-relaxed">
                Explore robots, deploy certified skills, hire humanoids, and discover strategic opportunities.
              </p>

              {/* Live Stats - Clean horizontal */}
              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
                <div className="text-center">
                  <div className="stat-value text-white">
                    {isLoading ? "—" : animatedRobots}
                  </div>
                  <div className="stat-label text-cyan-300/70 mt-1">Robots</div>
                </div>
                
                <div className="hidden sm:block h-12 w-px bg-white/10" />
                
                <div className="text-center">
                  <div className="stat-value text-white">
                    {isLoading ? "—" : animatedCompanies}
                  </div>
                  <div className="stat-label text-cyan-300/70 mt-1">Companies</div>
                </div>
                
                <div className="hidden sm:block h-12 w-px bg-white/10" />
                
                <div className="text-center">
                  <div className="stat-value text-white">
                    {isLoading ? "—" : animatedCountries}
                  </div>
                  <div className="stat-label text-cyan-300/70 mt-1">Countries</div>
                </div>
                
                {stats.deploymentCount > 0 && (
                  <>
                    <div className="hidden md:block h-12 w-px bg-white/10" />
                    <div className="text-center">
                      <div className="stat-value text-white">
                        {isLoading ? "—" : `${animatedDeployments.toLocaleString()}+`}
                      </div>
                      <div className="stat-label text-cyan-300/70 mt-1">Deployed</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* What is Lineroid - SEO Content Block */}
        <section className="py-16 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground mb-6">What is Lineroid?</h2>
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Lineroid is the global intelligence platform and marketplace for humanoid robotics. We provide comprehensive data 
                  on humanoid robots, the companies building them, certified software skills, and a peer-to-peer marketplace 
                  where you can hire humanoids for your operations.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong className="text-foreground">Why humanoid robots matter now:</strong> Global labor shortages, aging 
                  populations, and advances in AI are converging to make humanoid robots economically viable. Component costs 
                  are dropping 15-20% annually, while capabilities are improving exponentially.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">How to use Lineroid:</strong> Browse our <Link to="/robots" className="text-primary underline hover:text-primary/80">robot catalog</Link> to 
                  explore detailed specifications. Visit the <Link to="/marketplace" className="text-primary underline hover:text-primary/80">Skill Marketplace</Link> to 
                  deploy certified skills or hire robots for your operations. Use our <Link to="/opportunities" className="text-primary underline hover:text-primary/80">opportunity radar</Link> to 
                  identify strategic gaps and emerging niches.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Robots - Lazy loaded */}
        <Suspense fallback={<div className="py-20" />}>
          <FeaturedRobots robots={robots} isLoading={robotsLoading} />
        </Suspense>

        {/* Entry Points - Explore Section */}
        <section id="explore" className="py-20 border-b border-border">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <div className="text-center mb-12">
                <p className="text-sm font-mono text-primary uppercase tracking-widest mb-2">Explore</p>
                <h2 className="text-foreground">Navigate the Ecosystem</h2>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {entryPoints.map((entry, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Link
                    to={entry.href}
                    className={`group bg-card border rounded-lg p-6 hover:shadow-lg transition-all duration-300 block h-full relative ${
                      entry.highlight 
                        ? "border-primary/50 hover:border-primary" 
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <entry.icon className={`h-8 w-8 mb-4 ${entry.highlight ? 'text-primary' : 'text-muted-foreground group-hover:text-primary transition-colors'}`} />
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {entry.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{entry.description}</p>
                    <div className="flex items-center text-primary font-medium text-sm">
                      Explore
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                    {entry.highlight && (
                      <div className="absolute top-4 right-4">
                        <span className="text-xs font-mono text-primary uppercase tracking-wider">New</span>
                      </div>
                    )}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Skill Marketplace - Primary CTA Section */}
        <Suspense fallback={<div className="py-24" />}>
          <MarketplacePreview />
        </Suspense>

        {/* AI Recommender CTA - Lazy loaded */}
        <Suspense fallback={null}>
          <AIRecommenderCTA />
        </Suspense>

        {/* News Feed Preview - Lazy loaded */}
        <Suspense fallback={null}>
          <NewsFeedPreview />
        </Suspense>

        {/* Events Preview - Lazy loaded */}
        <Suspense fallback={null}>
          <EventsPreview />
        </Suspense>

        {/* Market Snapshot */}
        <section id="trends" className="py-20 border-b border-border">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <div className="text-center mb-12">
                <p className="text-sm font-mono text-primary uppercase tracking-widest mb-2">Market Trends</p>
                <h2 className="text-foreground">Key Drivers</h2>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
              {trends.map((trend, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Link 
                    to={`/market-snapshot/${trend.slug}`}
                    className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all h-full block"
                  >
                    <trend.icon className="h-8 w-8 text-primary mb-4" />
                    <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {trend.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{trend.description}</p>
                  </Link>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={300}>
              <div className="text-center">
                <Link to="/market-snapshot">
                  <Button variant="outline" size="sm">
                    View All Trends
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Intelligence & Insights Teaser */}
        <section id="intelligence" className="py-20 bg-muted/30 border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Intelligence Layer */}
              <AnimatedSection delay={0}>
                <div className="bg-card border border-border rounded-lg p-6 h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Intelligence</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                    Understand how the humanoid world fits together — from component suppliers to AI models to end buyers.
                  </p>
                  <div className="space-y-2">
                    <Link to="/ecosystem" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Ecosystem Map
                    </Link>
                    <Link to="/use-cases" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Use Case Library
                    </Link>
                    <Link to="/technology" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Technology Stack
                    </Link>
                  </div>
                </div>
              </AnimatedSection>

              {/* Insights & Reports */}
              <AnimatedSection delay={100}>
                <div className="bg-card border border-border rounded-lg p-6 h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Insights</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                    Deep analysis, market trends, and strategic reports to inform your decisions.
                  </p>
                  <div className="space-y-2">
                    <Link to="/insights" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Market Analysis
                    </Link>
                    <Link to="/reports" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Premium Reports
                    </Link>
                    <Link to="/compare" className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors">
                      <ArrowRight className="h-3 w-3 mr-2" /> Comparison Tool
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section id="newsletter" className="py-20">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <div className="max-w-xl mx-auto text-center">
                <p className="text-sm font-mono text-primary uppercase tracking-widest mb-2">Newsletter</p>
                <h2 className="text-foreground mb-4">Stay Ahead</h2>
                <p className="text-muted-foreground mb-8 leading-relaxed">
                  Weekly updates on new robots, deployments, funding rounds, and strategic opportunities.
                </p>
                <Link to="/newsletter">
                  <Button size="lg">
                    Subscribe
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>

      <FloatingSectionNav />
      <Footer />
    </div>
  );
};

export default Index;
